import React, { useState, useEffect } from 'react';
import './MarketTicker.css';

const MarketTicker = ({ userApiKeys }) => {
  const [tickerData, setTickerData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get API keys from props or environment
  const finnhubApiKey = userApiKeys?.finnhubApiKey || process.env.REACT_APP_FINNHUB_API_KEY || 'd3aijrhr01qlsbrrjh00d3aijrhr01qlsbrrjh0g';
  const useYahooFinance = userApiKeys?.yahooFinanceEnabled === true;
  const useFinHub = userApiKeys?.finnhubEnabled === true && finnhubApiKey && !useYahooFinance;
  
  console.log('MarketTicker: Finance module config - Yahoo Finance:', useYahooFinance, 'FinHub:', useFinHub);
  
  useEffect(() => {
    // Popular symbols to display in the ticker
    const SYMBOLS = [
      'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX',
      'BTC-USD', 'ETH-USD', 'EUR/USD', 'GBP/USD', 'JPY/USD', 'XAU/USD'
    ];

    const fetchTickerData = async () => {
      try {
        setLoading(true);
        
        // Check if any finance module is enabled
        if (!useYahooFinance && !useFinHub) {
          console.log('MarketTicker: No finance module enabled');
          setTickerData([]);
          return;
        }
        
        // Fetch quotes for all symbols with rate limiting
        const results = [];
        for (let i = 0; i < SYMBOLS.length; i++) {
          const symbol = SYMBOLS[i];
          
          try {
            // Add small delay between requests to respect rate limits
            if (i > 0) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            let data = null;
            
            if (useYahooFinance) {
              // Use Yahoo Finance 2 via backend API
              const response = await fetch(`http://localhost:3001/api/yahoo-quote/${symbol}`);
              if (response.ok) {
                data = await response.json();
                if (data && data.price) {
                  results.push({
                    symbol,
                    price: data.price,
                    change: data.change,
                    changePercent: data.changePercent,
                    timestamp: Date.now()
                  });
                }
              }
            } else if (useFinHub) {
              // Use FinHub API
              const response = await fetch(
                `https://finnhub.io/api/v1/quote?symbol=${symbol}&token=${finnhubApiKey}`
              );
              
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              
              data = await response.json();
              
              // Skip if no data or invalid response
              if (!data || data.c === 0) {
                continue;
              }
              
              results.push({
                symbol,
                price: data.c,
                change: data.d,
                changePercent: data.dp,
                timestamp: Date.now()
              });
            }
          } catch (error) {
            console.error(`Error fetching ticker data for ${symbol}:`, error);
            continue;
          }
        }
        
        setTickerData(results);
      } catch (error) {
        console.error('Error fetching ticker data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchTickerData();
    
    // Update every 2 minutes to stay within rate limits
    const interval = setInterval(fetchTickerData, 120000);
    
    return () => clearInterval(interval);
  }, [finnhubApiKey, useYahooFinance, useFinHub]);

  const formatPrice = (price, symbol) => {
    if (symbol.includes('BTC') || symbol.includes('ETH')) {
      return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    } else if (symbol.includes('/USD') || symbol.includes('EUR') || symbol.includes('GBP') || symbol.includes('JPY')) {
      return price.toFixed(4);
    } else if (symbol.includes('XAU')) {
      return `$${price.toFixed(2)}`;
    } else {
      return `$${price.toFixed(2)}`;
    }
  };

  const formatChange = (change, changePercent) => {
    const isPositive = change >= 0;
    const sign = isPositive ? '+' : '';
    
    return (
      <span className={`ticker-change ${isPositive ? 'positive' : 'negative'}`}>
        {sign}{changePercent?.toFixed(2)}% ({sign}{change?.toFixed(2)})
      </span>
    );
  };

  if (loading) {
    return (
      <div className="market-ticker">
        <div className="ticker-container">
          <div className="ticker-loading">Loading market data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="market-ticker">
      <div className="ticker-container">
        <div className="ticker-scroll">
          {tickerData.map((item, index) => (
            <div key={`${item.symbol}-${index}`} className="ticker-item">
              <div className="ticker-symbol">{item.symbol}</div>
              <div className="ticker-price">{formatPrice(item.price, item.symbol)}</div>
              <div className="ticker-change-container">
                {formatChange(item.change, item.changePercent)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketTicker;
